import 'dayjs/locale/es';

import { Box } from '@mui/material';
import dayjs from 'dayjs';
import {
  getGeneralData,
  getPurchaseOrders,
  getTenders,
} from 'lib/api/publicMarket';
import { SummaryResponse } from 'lib/api/publicMarket';
import GlobalSearcher, {
  Filter as GlobalFilter,
} from 'lib/common/GlobalSearcher';
import { PURCHASE_ORDER_STATES, TENDER_STATES } from 'lib/consts';
import EnhancedTab from 'lib/designSystem/TabSimulation';
import {
  PurchaseOrder,
  PurchaseOrderType,
  Tender,
  TenderType,
} from 'lib/types/models';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import PurchaseOrders from './components/PurchaseOrders';
import Summary from './components/Summary';
import Tenders from './components/Tenders';

dayjs.locale('es');

const BASE_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

const PublicMarket = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = React.useState<number>(0);
  const [pagination, setPagination] = useState(BASE_PAGINATION);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingTenders, setLoadingTenders] = useState(false);
  const [loadingPurchaseOrders, setLoadingPurchaseOrders] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [summaryData, setSummaryData] = useState<SummaryResponse | null>(null);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [dateOptions, setDateOptions] = useState<string>('publication');
  const [externalCode, setExternalCode] = useState<string>('');
  const [selectedBuyer, setSelectedBuyer] = useState<string>('');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [status, setStatus] = useState<{ value: string; label: string }[]>([]);
  const [types, setTypes] = useState<
    { value: TenderType | PurchaseOrderType; label: string }[]
  >([]);
  const [initialDateStr, setInitialDateStr] = useState<string | null>(
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  );
  const [finalDateStr, setFinalDateStr] = useState<string | null>(
    dayjs().format('YYYY-MM-DD'),
  );
  const tenderTypeOptions = Object.values(TenderType);
  const purchaseOrderTypeOptions = Object.values(PurchaseOrderType);

  const statusOptions = useMemo(
    () =>
      activeTab === 1
        ? TENDER_STATES
        : activeTab === 2
          ? PURCHASE_ORDER_STATES
          : [],
    [activeTab],
  );

  const globalFilters: (GlobalFilter | null)[] = [
    {
      type: 'date',
      label: 'Fecha inicio',
      dateKey: 'startDate',
      value: initialDateStr,
      setter: setInitialDateStr,
    },
    {
      type: 'date',
      label: 'Fecha fin',
      dateKey: 'endDate',
      value: finalDateStr,
      setter: setFinalDateStr,
    },
    activeTab !== 2
      ? {
          type: 'select',
          label: 'Fecha',
          value: dateOptions,
          setter: setDateOptions,
          options: [
            { value: 'publication', label: 'Publicación' },
            { value: 'adjudication', label: 'Adjudicación' },
          ],
        }
      : null,
    {
      type: 'text',
      label: 'Código',
      value: externalCode,
      setter: setExternalCode,
    },
    {
      type: 'multiple',
      label: 'Tipo',
      value: types,
      setter: setTypes,
      options:
        activeTab === 1
          ? tenderTypeOptions.map((t) => ({ value: t, label: t }))
          : purchaseOrderTypeOptions.map((t) => ({ value: t, label: t })),
    },
    {
      type: 'autocomplete',
      label: 'Mandante',
      key: 'buyer',
      value: selectedBuyer,
      setter: setSelectedBuyer,
    },
    activeTab === 2
      ? {
          type: 'autocomplete',
          label: 'Proveedor',
          key: 'supplier',
          value: selectedSupplier,
          setter: setSelectedSupplier,
        }
      : null,
    activeTab !== 0
      ? {
          type: 'multiple',
          label: 'Estado',
          value: status,
          setter: setStatus,
          options: statusOptions.map((s) => ({
            value: String(s.value),
            label: s.label,
          })),
        }
      : null,
  ];

  const buildSummaryQuery = (): string => {
    const params = new URLSearchParams();
    if (selectedBuyer) params.append('buyerId', selectedBuyer);
    if (types.length > 0)
      params.append('types', types.map((t) => t.value).join(','));
    if (initialDateStr) params.append('initialDate', initialDateStr);
    if (finalDateStr) params.append('finalDate', finalDateStr);
    return params.toString();
  };

  const fetchSummary = async () => {
    setLoadingSummary(true);
    try {
      const queryString = buildSummaryQuery();
      const response = await getGeneralData(dispatch, queryString);
      setSummaryData(response ?? null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const buildTendersQuery = (override?: {
    page?: number;
    pageSize?: number;
  }): string => {
    const params = new URLSearchParams();
    if (externalCode) params.append('externalCode', externalCode);
    if (selectedBuyer) params.append('buyerId', selectedBuyer);
    if (types.length > 0)
      params.append('types', types.map((t) => t.value).join(','));
    if (initialDateStr)
      params.append(
        dateOptions === 'publication'
          ? 'startPublicationDate'
          : 'startAdjudicationDate',
        initialDateStr,
      );
    if (finalDateStr)
      params.append(
        dateOptions === 'publication'
          ? 'endPublicationDate'
          : 'endAdjudicationDate',
        finalDateStr,
      );
    if (status.length > 0)
      params.append('status', status.map((s) => s.value).join(','));
    const pageToUse = override?.page ?? pagination.page;
    const pageSizeToUse = override?.pageSize ?? pagination.pageSize;
    if (pageToUse) params.append('page', String(pageToUse));
    if (pageSizeToUse) params.append('pageSize', String(pageSizeToUse));
    return params.toString();
  };

  const fetchTenders = async (params?: { page: number; pageSize: number }) => {
    try {
      setLoadingTenders(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const queryString = buildTendersQuery({
        page: pageToUse,
        pageSize: pageSizeToUse,
      });
      const response = await getTenders(dispatch, queryString);
      setTenders(response?.data ?? []);
      setPagination(response?.pagination ?? BASE_PAGINATION);
      return {
        data: response?.data ?? [],
        pagination: response?.pagination ?? BASE_PAGINATION,
      };
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingTenders(false);
    }
  };

  const buildPurchaseOrdersQuery = (override?: {
    page?: number;
    pageSize?: number;
  }): string => {
    const params = new URLSearchParams();
    if (externalCode) params.append('externalCode', externalCode);
    if (selectedBuyer) params.append('buyerId', selectedBuyer);
    if (selectedSupplier) params.append('supplierId', selectedSupplier);
    if (types.length > 0)
      params.append('types', types.map((t) => t.value).join(','));
    if (status.length > 0)
      params.append('status', status.map((s) => s.value).join(','));
    if (initialDateStr) params.append('startCreationDate', initialDateStr);
    if (finalDateStr) params.append('endCreationDate', finalDateStr);
    const pageToUse = override?.page ?? pagination.page;
    const pageSizeToUse = override?.pageSize ?? pagination.pageSize;
    if (pageToUse) params.append('page', String(pageToUse));
    if (pageSizeToUse) params.append('pageSize', String(pageSizeToUse));
    return params.toString();
  };

  const fetchPurchaseOrders = async (params?: {
    page: number;
    pageSize: number;
  }) => {
    try {
      setLoadingPurchaseOrders(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const queryString = buildPurchaseOrdersQuery({
        page: pageToUse,
        pageSize: pageSizeToUse,
      });
      const response = await getPurchaseOrders(dispatch, queryString);
      setPurchaseOrders(response?.data ?? []);
      setPagination(response?.pagination ?? BASE_PAGINATION);
      return {
        data: response?.data ?? [],
        pagination: response?.pagination ?? BASE_PAGINATION,
      };
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPurchaseOrders(false);
    }
  };

  const changeTab = (tab: number) => {
    setInitialDateStr(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
    setFinalDateStr(dayjs().format('YYYY-MM-DD'));
    setDateOptions('publication');
    setExternalCode('');
    setSelectedBuyer('');
    setSelectedSupplier('');
    setStatus([]);
    setTypes([]);
    setActiveTab(tab);
    setPagination(BASE_PAGINATION);
  };

  const fetchData = async () => {
    if (activeTab === 0) await fetchSummary();
    else if (activeTab === 1)
      await fetchTenders({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
    else if (activeTab === 2)
      await fetchPurchaseOrders({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
  };

  useEffect(() => {
    if (activeTab === 0) void fetchSummary();
    else if (activeTab === 1)
      void fetchTenders({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
    else if (activeTab === 2)
      void fetchPurchaseOrders({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
  }, [activeTab]);

  return (
    <Box sx={{ position: 'relative' }}>
      <EnhancedTab
        startValue={0}
        tabs={[
          {
            label: 'Resumen',
            component: <Summary loading={loadingSummary} data={summaryData} />,
          },
          {
            label: 'Licitaciones',
            component: (
              <Tenders
                loading={loadingTenders}
                fetchData={(params?: { page: number; pageSize: number }) =>
                  fetchTenders(params)
                }
                data={tenders}
                setData={setTenders}
                pagination={pagination}
                setPagination={setPagination}
              />
            ),
          },
          {
            label: 'Ordenes de compra',
            component: (
              <PurchaseOrders
                loading={loadingPurchaseOrders}
                fetchData={(params?: { page: number; pageSize: number }) =>
                  fetchPurchaseOrders(params)
                }
                data={purchaseOrders}
                setData={setPurchaseOrders}
                pagination={pagination}
                setPagination={setPagination}
              />
            ),
          },
        ]}
        onTabChange={changeTab}
      />
      <Box sx={{ my: 2, position: 'absolute', top: 0, right: 0 }}>
        <GlobalSearcher
          filters={globalFilters.filter(Boolean) as GlobalFilter[]}
          triggerSearch={() => fetchData()}
          dispatch={dispatch}
        />
      </Box>
    </Box>
  );
};

export default PublicMarket;
